import FractionCalculator from "../MathTools/FractionCalculator";
import LinearGraphTool from "../MathTools/LinearGraphTool";
import DataAnalyzer from "../MathTools/DataAnalyzer";
import RatioPercentConverter from "../MathTools/RatioPercentConverter";
import RectPrismTool from "../MathTools/RectPrismTool";

export const chapterPages = {
    1: [
      {
        title: <h1>Ratios, Rates & Percents</h1>,
        body: (
          <>
            <p>
              <strong>Ratio</strong> – compares two amounts with the same unit.<br/>
              <strong>Rate</strong> – a ratio for two amounts with different units 
              (e.g., km per hour).<br/>
              <strong>Percent</strong> – a special ratio meaning “out of 100.”
            </p>
            <p>
              For example, if a recipe calls for <strong>4 cups of flour</strong> 
              and <strong>3 cups of sugar</strong>, we can represent that in
              ratio form (<code>4 : 3</code>), fraction form (<code>4/3</code>), 
              or as a percent (~133% because 4 ÷ 3 ≈ 1.33, then ×100).
            </p>
            <p>
              Being able to convert easily between these forms is crucial in
              everyday math—like scaling recipes or calculating discounts
              and sales tax.
            </p>
          </>
        )
      },
      {
        title: <h1>Unit Rates & Percent of a Quantity</h1>,
        body: (
          <>
            <p>
              A <strong>unit rate</strong> tells you the amount per 1 unit. You
              find it by dividing the total by how many units. For instance,
              180 km in 3 hours means 60 km per hour.
            </p>
            <p>
              To find a <strong>percent of a quantity</strong>, convert the
              percent to a decimal, then multiply by that quantity:
            </p>
            <p>
              Example: <code>15%</code> of 80 → 0.15 × 80 = 12
            </p>
            <p>
              Below is a simple Ratio/Percent converter tool to practice
              flipping between forms:
            </p>
            <RatioPercentConverter />
            <p>
              Try some different ratios or percents above, then see how
              everything matches up in real time!
            </p>
          </>
        )
      }
    ],
  
    2: [
      {
        title: <h1>Fraction Operations – Grade 8 Mastery</h1>,
        body: (
          <>
            {/* Big‑picture WHY */}
            <p>
              Grade 8 moves beyond basic fraction skills.  You’re expected to
              <strong>combine multiple operations in one expression</strong>, work
              confidently with <em>negative</em> and <em>mixed‑sign</em> terms, and
              simplify complex fractions.  The checklist:
            </p>
            <ul>
              <li>Apply <strong>order of operations</strong> (BEDMAS) when fractions & integers mix.</li>
              <li>Interpret and simplify <strong>complex fractions</strong> (fractions whose numerators and/or denominators are themselves fractions).</li>
              <li>Convert between <em>mixed numbers</em>, <em>improper fractions</em>, and <em>terminating/repeating decimals</em>.</li>
              <li>Solve <strong>multi‑step word problems</strong> that embed any of the above.</li>
            </ul>
    
            {/* Example 1 – multi‑step expression */}
            <h4>Example 1 – Evaluate</h4>
            <pre style={{background:'#f5f5f5',padding:8}}>
      3&nbsp;½  −  [ 2⁄3  ×  (−4⁄9) ]  ÷  ¾
            </pre>
            <ol>
              <li>Convert <code>3½</code> → <code>7⁄2</code>.</li>
              <li>Work inside the brackets: <code>2⁄3 × (−4⁄9) = −8⁄27</code>.</li>
              <li>Division by <code>¾</code> ⇒ multiply by its reciprocal <code>4⁄3</code>.</li>
              <li>…and so on. <em>(Full walkthrough left for the interactive tool.)</em></li>
            </ol>
    
            {/* Example 2 – complex fraction */}
            <h4>Example 2 – Simplify</h4>
            <pre style={{background:'#f5f5f5',padding:8}}>
          (5⁄6)  ÷  ( 7⁄12 )
            </pre>
            <p>
              ➜ Multiply by the reciprocal → <code>5⁄6 × 12⁄7</code>, reduce by 6,
              final answer <code>10⁄7</code> ≈ 1 <sup>3</sup>⁄<sub>7</sub>.
            </p>
    
            <p style={{marginTop:16}}>
              On the next page you’ll try similar questions in an interactive
              practice zone.
            </p>
          </>
        )
      },
    
      {
        title: <h1>Mixed, Improper & Decimal Forms</h1>,
        body: (
          <>
            <p>
              Switching forms is a <strong>strategy choice</strong>.  Some problems
              are faster in mixed‑number form (easier mental subtraction), while
              others are cleaner as improper fractions for multiplication/division.
            </p>
            <h4>Converting Repeating Decimals ➜ Fractions</h4>
            <p>
              Example: turn <code>0.7\overline{3}</code> into a fraction.
            </p>
            <ol>
              <li>Let <code>x = 0.7333…</code></li>
              <li>Multiply by 10 → <code>10x = 7.333… </code></li>
              <li>Subtract <code>x</code> → <code>9x = 6.6</code></li>
              <li>So <code>x = 6.6⁄9 = 66⁄90 = 11⁄15</code>.</li>
            </ol>
    
            <h4>Fast Checks</h4>
            <ul>
              <li><strong>Does the sign make sense?</strong> A negative × negative should be positive.</li>
              <li><strong>Is the fraction simplified?</strong> Divide numerator & denominator by their GCF.</li>
            </ul>
    
            <p>
              Try the calculator below to flip between forms and verify your steps.
            </p>
            <FractionCalculator />
          </>
        ),
        isFractionInteractive: true
      },
    
      {
        title: <h1>Complex Fractions & Order of Operations</h1>,
        body: (
          <>
            <p>
              A <strong>complex fraction</strong> looks intimidating, but the key is
              to <em>clear the smaller denominators first</em> so that you’re left
              with one tidy numerator over one tidy denominator.
            </p>
            <pre style={{background:'#f5f5f5',padding:8}}>
            ( 1 + 2⁄3 )  ⁄  ( 4⁄5 − 1⁄10 )
            </pre>
            <p>
              Multiply top & bottom by the Least Common Denominator (30) to clear
              the small fractions before simplifying.
            </p>
    
            <h4>Mixed Operation Word Problem</h4>
            <p>
              <strong>"A recipe needs 2 ¾ cups of flour for one batch. How much flour
              is required for 3½ batches, and how many <em>full</em> cups of flour
              will be left if you start with a 5‑kilogram bag?"</strong>
            </p>
            <p>
              Problems like this demand both form‑switching and unit‑conversion
              (find mass‑to‑volume ratio first!).  You’ll meet similar tasks in the
              chapter quiz.
            </p>
          </>
        )
      }  
    ],
  
    3: [
      {
        title: <h1>Patterns & Linear Relations</h1>,
        body: (
          <>
            <p>
              A <strong>discrete linear relation</strong> is a pattern of inputs and outputs
              that grows by a constant amount each step. You can represent it three ways:
            </p>
            <ul>
              <li>
                <strong>Table:</strong> list a few (x, y) pairs, e.g.:
                <table style={{margin: "8px 0", borderCollapse: "collapse"}}>
                  <thead>
                    <tr><th style={{border: "1px solid #aaa", padding: 4}}> x </th>
                        <th style={{border: "1px solid #aaa", padding: 4}}> y </th></tr>
                  </thead>
                  <tbody>
                    <tr><td style={{border: "1px solid #aaa", padding: 4}}>0</td>
                        <td style={{border: "1px solid #aaa", padding: 4}}>2</td></tr>
                    <tr><td style={{border: "1px solid #aaa", padding: 4}}>1</td>
                        <td style={{border: "1px solid #aaa", padding: 4}}>5</td></tr>
                    <tr><td style={{border: "1px solid #aaa", padding: 4}}>2</td>
                        <td style={{border: "1px solid #aaa", padding: 4}}>8</td></tr>
                  </tbody>
                </table>
              </li>
              <li>
                <strong>Graph:</strong> plot those points on a grid—if they lie on a straight
                line, you’ve got a linear relation.
              </li>
              <li>
                <strong>Rule:</strong> an equation in the form <code>y = m x + b</code>, where
                <ul>
                  <li><strong>m</strong> (the slope) = “rise/run” = how much y changes when x increases by 1</li>
                  <li><strong>b</strong> (the y‑intercept) = the value of y when x = 0</li>
                </ul>
              </li>
            </ul>
      
            <h4>Worked Example</h4>
            <div className="worked-example">
    <div className="worked-example__graph"></div>
            <LinearGraphTool rule="y = 3x + 2" widthCells={10} heightCells={10}/>
            </div>
            <div className="worked-example__text">
            <p>
              Suppose the rule is <code>y = 3x + 2</code>:
            </p>
            <ol>
              <li>Start at (0, 2) because when x=0, y=2.</li>
              <li>Since <strong>m=3</strong>, each time x goes up by 1, y goes up by 3:
                (1, 5), (2, 8), (3, 11), …</li>
              <li>Plot those points and draw your straight line through them.</li>
            </ol></div>
      
            <h4>Reverse Engineering</h4>
            <p>
              Given points (1, 4) and (4, 13):
            </p>
  <ol>
    <li>
      Find the slope:<br/>
      <code>
        m ={' '}
        <span className="fraction">
          <span className="num">13 − 4</span>
          <span className="den">4 − 1</span>
        </span>{' '}
        ={' '}
        <span className="fraction">
          <span className="num">9</span>
          <span className="den">3</span>
        </span>{' '}
        = 3
      </code>
    </li>
    <li>
      Plug into <code>y = mx + b</code>:<br/>
      <code>
        4 = 3·1 + b ⇒ b = 1
      </code>
    </li>
    <li>
      So the rule is <code>y = 3x + 1</code>. Check with (4, 13):<br/>
      <code>
        13 = 3·4 + 1 ✔️
      </code>
    </li>
  </ol>
            <p>
              Understanding tables ⇄ graphs ⇄ rules means you can tackle any representation
              that shows a constant‐rate pattern.
            </p>
          </>
        )
      },
      {
        title: <h1>Graph ↔ Table ↔ Rule Converter</h1>,
        body: (
          <>
            <p>
              This interactive tool helps you move seamlessly between three representations of a linear pattern:
            </p>
            <ul>
              <li>
                <strong>Table:</strong> Enter a few&nbsp;(x,y) pairs in the fields below.
              </li>
              <li>
                <strong>Graph:</strong> Click “Plot Points” and watch them appear on the coordinate grid.
              </li>
              <li>
                <strong>Rule:</strong> The tool will compute the slope <em>m</em>, the intercept <em>b</em>, and show you 
                the equation <code>y = m x + b</code>.
              </li>
            </ul>
            <p>
              <em>How to use it:</em>
            </p>
            <ol>
              <li>Type in at least two points—for example <code>(0, 1)</code> and <code>(2, 5)</code>.</li>
              <li>Hit “Plot Points” to draw them on the grid and automatically fit the line.</li>
              <li>
                Check the outputs:
                <ul>
                  <li><strong>Slope</strong> = rise/run</li>
                  <li><strong>Y‑intercept</strong> = where the line crosses the y‑axis</li>
                  <li><strong>Equation</strong> = <code>y = m x + b</code></li>
                </ul>
              </li>
              <li>To try a new set, click “Clear All,” then enter your next points.</li>
            </ol>
            <LinearGraphTool widthCells={12} heightCells={12} />
            <p style={{ marginTop: 16 }}>
              By entering different points and plotting them, you’ll see exactly how tables, graphs, and algebraic rules all describe the same constant‑rate pattern.
            </p>
          </>
        ),
        isLinearInteractive: true
      } 
    ],
  
    4: [
      {
        title: <h1>Surface Area vs Volume</h1>,
        body: (
          <>
            {/* 1. Big picture */}
            <p>
              Every 3D shape has two different “sizes”:
            </p>
            <ul>
              <li>
                <strong>Volume</strong> tells you <em>how much it can hold</em>—like the water inside a juice box.
              </li>
              <li>
                <strong>Surface Area</strong> tells you <em>how much skin it has</em>—like how much wrapping paper you need to cover that juice box.
              </li>
            </ul>
      
            {/* 2. Real‑world example */}
            <h4>Juice Box vs. Can</h4>
            <p>
              Imagine two containers that both hold <strong>250 mL</strong> of juice:
            </p>
            <ul>
              <li>
                A <em>short, wide</em> box—lots of faces, so more wrapping paper.
              </li>
              <li>
                A <em>tall, skinny</em> can—fewer faces, so less wrapping paper.
              </li>
            </ul>
            <p style={{ fontStyle: "italic" }}>
              Same volume, very different surface area!
            </p>
      
            {/* 3. Formulas (translated into words) */}
            <h4>Formulas to Remember</h4>
            <p>
              <strong>Rectangular Prism</strong> (like a shoebox):
            </p>
            <blockquote style={{ margin: "8px 0", paddingLeft: 12, borderLeft: "3px solid #ccc" }}>
              • <em>Volume</em> = length × width × height  
              <br/>
              • <em>Surface Area</em> = 2 × (lw + lh + wh)  
            </blockquote>
            <p>
              <strong>Cylinder</strong> (like a can):
            </p>
            <blockquote style={{ margin: "8px 0", paddingLeft: 12, borderLeft: "3px solid #ccc" }}>
              • <em>Volume</em> = π × r² × height  
              <br/>
              • <em>Surface Area</em> = 2 × π × r × h  +  2 × π × r²  
            </blockquote>
      
          </>
        )
      },
      {
        title: <h1>3‑D Shape Explorer</h1>,
        body: (
          <>
          {/* 4. Why it matters */}
          <p>
              <strong>Why learn both?</strong><br/>
              • If you’re packing (volume), you care about capacity.  
              • If you’re painting or wrapping (surface area), you care about the outside skin.
            </p>
            <p>
              Below is a tool where you can change length, width, height
              (or radius, etc.) to see how volume and surface area respond:
            </p>
            <RectPrismTool />
            <p>
              Explore how changing one dimension affects volume more
              dramatically than surface area, and vice versa.
            </p>
          </>
        ),
        isSurfaceInteractive: true
      }
    ],
  
    5: [
      {
        title: <h1>Statistics – Measures of Centre</h1>,
        body: (
          <>
            <p>
              You already know <strong>mean</strong>, <strong>median</strong>, and <strong>mode</strong> as ways to summarize a set of data. In Grade 8, we focus on <em>choosing</em> which one best represents the “middle” when our data has <strong>skew</strong> or <strong>outliers</strong>.
            </p>
            <ul>
              <li>
                <strong>Mean</strong>: the arithmetic average (sum of all values ÷ number of values). Great for data that’s spread out evenly, but can be pulled up or down by very large or very small values.
              </li>
              <li>
                <strong>Median</strong>: the middle value once your data is sorted from smallest to largest. It splits your data in half and isn’t affected by extreme values.
              </li>
              <li>
                <strong>Mode</strong>: the most frequent value(s). Handy for finding the “most common” score or category.
              </li>
            </ul>
            <h4>When to use which?</h4>
            <p>
              • If your data is <strong>symmetrical</strong> with no big outliers, the <em>mean</em> is a fine choice.<br/>
              • If your data is <strong>skewed</strong> or has outliers, the <em>median</em> gives a better “middle.”<br/>
              • If you want the most common result, use the <em>mode</em>.
            </p>
            <h4>Quick Example</h4>
            <p>
              Data: [2, 5, 7, 7, 20]<br/>
              Mean = (2+5+7+7+20)/5 = 41/5 = 8.2<br/>
              Median = 7 (middle of the sorted list)<br/>
              Mode = 7 (the value that appears most often)
            </p>
          </>
        )
      },
      {
        title: <h1>Box‑and‑Whisker Plots & Histograms</h1>,
        body: (
          <>
            <p>
              Drawing your data helps you see its shape—whether it’s bunched up, has long tails, or contains outliers—and decide if you should use mean or median.
            </p>
            <h4>Box‑and‑Whisker Plot</h4>
            <ul>
              <li>The box spans the 1st quartile (Q1) to the 3rd quartile (Q3).</li>
              <li>The line inside the box marks the median.</li>
              <li>“Whiskers” extend to the smallest and largest non‑outlier points.</li>
              <li>Any point more than 1.5×IQR (inter‑quartile range) from Q1 or Q3 is shown as an outlier.</li>
            </ul>
            <h4>Histogram</h4>
            <ul>
              <li>Bars group data into equal‑width bins and show how many values fall into each.</li>
              <li>Tall, narrow bars mean many values in that range; gaps or long tails reveal skew.</li>
            </ul>
            <p>
              Use the tool below to enter or generate a small data set, then toggle between a histogram and a box plot to see how median, mean, and mode line up with the shape of your data.
            </p>
            <DataAnalyzer />
          </>
        ),
        isStatsInteractive: true
      }
    ],
  
    6: [
      {
        title: <h1>Grade 8 Summary</h1>,
        body: (
          <>
            <p>
              Well done!  You’ve covered: Ratio/Percent, Fraction Operations,
              Linear Relations, Surface vs Volume, and Statistics.
            </p>
            <p>
              Now you can try a final quiz of 50 questions across all
              chapters. Good luck!
            </p>
          </>
        )
      }
    ]
  };